import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { UserApi } from "../../api/UserApi";
import type { CreateUserStaff } from "../../generated/dto/CreateUserStaff";
import type { DeleteUserStaff } from "../../generated/dto/DeleteUserStaff";
import type { RoleAssignmentFetchOptions } from "../../generated/dto/RoleAssignmentFetchOptions";
import type { RoleAssignmentStaff } from "../../generated/dto/RoleAssignmentStaff";
import type { UpdateUserStaff } from "../../generated/dto/UpdateUserStaff";
import type { UserPermissionFetchOptions } from "../../generated/dto/UserPermissionFetchOptions";
import type { UserPermissionStaff } from "../../generated/dto/UserPermissionStaff";
import type { UserStaff } from "../../generated/dto/UserStaff";
import type { UserStaffFetchOptions } from "../../generated/dto/UserStaffFetchOptions";

export class UserApiClient implements UserApi {
  constructor(private readonly client = useClient()) {}

  async getUserStaff(id: number, options?: UserStaffFetchOptions): Promise<UserStaff> {
    const { data } = await this.client.get<unknown>(`/user/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.UserStaffFetchOptions") });
    return deserializeCbiDto<UserStaff>(data, "generated.dto.UserStaff");
  }

  async createUserStaff(source: CreateUserStaff): Promise<UserStaff> {
    const { data } = await this.client.post<unknown>(`/user/staff`, serializeCbiDto(source, "generated.dto.CreateUserStaff"));
    return deserializeCbiDto<UserStaff>(data, "generated.dto.UserStaff");
  }

  async updateUserStaff(source: UpdateUserStaff): Promise<UserStaff> {
    const { data } = await this.client.put<unknown>(`/user/staff/${source.userId}`, serializeCbiDto(source, "generated.dto.UpdateUserStaff"));
    return deserializeCbiDto<UserStaff>(data, "generated.dto.UserStaff");
  }

  async deleteUserStaff(source: DeleteUserStaff): Promise<void> {
    await this.client.delete(`/user/staff/${source.userId}`);
  }

  async getUserStaffRoleAssignments(id: number, options?: RoleAssignmentFetchOptions): Promise<Array<RoleAssignmentStaff>> {
    const { data } = await this.client.get<unknown>(`/user/staff/${id}/roleassignments`, { params: serializeCbiDto(options, "generated.dto.RoleAssignmentFetchOptions") });
    return deserializeCbiDto<Array<RoleAssignmentStaff>>(data, "generated.dto.RoleAssignmentStaff");
  }

  async getUserStaffPermissions(id: number, options?: UserPermissionFetchOptions): Promise<Array<UserPermissionStaff>> {
    const { data } = await this.client.get<unknown>(`/user/staff/${id}/permissions`, { params: serializeCbiDto(options, "generated.dto.UserPermissionFetchOptions") });
    return deserializeCbiDto<Array<UserPermissionStaff>>(data, "generated.dto.UserPermissionStaff");
  }

  async currentUserStaff(): Promise<UserStaff> {
    const { data } = await this.client.post<unknown>(`/user/staff/currentuser`);
    let result = data as UserStaff;
    result = deserializeCbiDto<UserStaff>(result, "generated.dto.UserStaff");
    return result;
  }
}

export const useUserApi = (): UserApi => new UserApiClient();
